import {
	ApiError,
	type PortalOrder,
	type PortalReturnRequest,
	type PortalShipment,
} from "@khazzin/api-client";
import { formatDateTime } from "@khazzin/i18n/dates";
import { StatusChip } from "@khazzin/ui/patterns";
import { KeyValueList } from "@khazzin/ui/patterns/page";
import { Button, ConfirmDialog, Input, NumberInput, Select } from "@khazzin/ui/primitives";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { useServiceOff } from "../../app/shell/serviceSwitches";
import { ReportProblemButton } from "../tickets/OpenTicketDrawer";
import { Photos, returnsKey } from "./ReturnsPage";

/**
 * The order detail's part of EP10-S14 (DA-10-05, DA-10-08): Request return on
 * a delivered order, each shipment with Recall or Approve RTO where the store
 * may, and the Proof of delivery card — the photos and signature from links
 * that expire, the code-verified badge, who took the parcel and when, and
 * whether it was taken at the address. No driver is named anywhere.
 */

const REASONS = ["CUSTOMER_REMORSE", "WRONG_ITEM", "DAMAGED", "NOT_AS_DESCRIBED", "OTHER"] as const;

type Reason = (typeof REASONS)[number];

function shipmentsKey(storeId: string, orderId: string) {
	return ["portal", storeId, "orders", "shipments", orderId] as const;
}

/** A delivered order's lines may come back: what was delivered and not yet returned. */
export function RequestReturnButton({ order }: { order: PortalOrder }) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const { store } = useStoreScope();
	const storeId = store?.storeId ?? "";
	const [open, setOpen] = useState(false);
	const [reason, setReason] = useState<Reason>("CUSTOMER_REMORSE");
	const [notes, setNotes] = useState("");
	const [qty, setQty] = useState<Record<string, string>>({});
	const [failed, setFailed] = useState<string | null>(null);
	const off = useServiceOff(storeId);
	const o = order.order;
	const returnable = order.lines
		.map((line) => ({ line, max: line.qtyDelivered - line.qtyReturned }))
		.filter(({ max }) => max > 0);
	if (
		levelOf(store, "returns") !== "write" ||
		off("RETURNS") ||
		(o.status !== "delivered" && o.status !== "partially_delivered") ||
		returnable.length === 0
	) {
		return null;
	}
	return (
		<>
			<Button
				variant="secondary"
				onClick={() => {
					setFailed(null);
					setOpen(true);
				}}
			>
				{t("portal.returns.request.button")}
			</Button>
			<ConfirmDialog
				open={open}
				onOpenChange={setOpen}
				title={t("portal.returns.request.title", { orderNo: o.orderNo })}
				description={t("portal.returns.request.body")}
				confirmLabel={t("portal.returns.request.submit")}
				cancelLabel={t("ui.actions.cancel")}
				closeLabel={t("ui.actions.close")}
				onConfirm={async () => {
					const lines = returnable
						.map(({ line }) => ({
							orderLineId: line.orderLineId,
							qtyExpected: Number(qty[line.orderLineId] ?? "0"),
						}))
						.filter((line) => Number.isInteger(line.qtyExpected) && line.qtyExpected > 0);
					if (lines.length === 0) {
						setFailed(t("portal.returns.request.none"));
						throw new Error("no lines");
					}
					const body: PortalReturnRequest = {
						kind: "return",
						orderId: o.orderId,
						reasonCode: reason,
						lines,
						...(notes.trim() === "" ? {} : { notes: notes.trim() }),
					};
					try {
						await api.requestPortalReturn(body);
					} catch (error) {
						setFailed(error instanceof ApiError ? error.message : t("portal.returns.refused"));
						throw error;
					}
					await Promise.all([
						queries.invalidateQueries({ queryKey: ["portal", storeId, "orders"] }),
						queries.invalidateQueries({ queryKey: returnsKey(storeId) }),
					]);
				}}
			>
				<div className="flex flex-col gap-3">
					<table className="w-full text-[13px]">
						<thead className="text-fg-muted">
							<tr>
								<th className="py-1 text-start font-semibold">
									{t("portal.orders.detail.line.sku")}
								</th>
								<th className="py-1 text-end font-semibold">{t("portal.returns.request.qty")}</th>
							</tr>
						</thead>
						<tbody>
							{returnable.map(({ line, max }) => (
								<tr key={line.orderLineId} className="border-t border-border">
									<td className="py-1.5">
										<span className="font-mono">{line.sku}</span>
										{line.title ? ` · ${line.title}` : ""}
									</td>
									<td className="w-28 py-1.5">
										<NumberInput
											integerOnly
											aria-label={t("portal.returns.request.qtyOf", { sku: line.sku, max })}
											value={qty[line.orderLineId] ?? ""}
											placeholder="0"
											onChange={(e) => {
												const n = Math.min(Number(e.target.value), max);
												setQty({
													...qty,
													[line.orderLineId]: e.target.value === "" ? "" : String(n),
												});
											}}
										/>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<Select
						aria-label={t("portal.returns.request.reason")}
						value={reason}
						onValueChange={(next) => setReason(next as Reason)}
						options={REASONS.map((value) => ({
							value,
							label: t(`portal.returns.reason.${value}`),
						}))}
					/>
					<Input
						aria-label={t("portal.returns.request.notes")}
						placeholder={t("portal.returns.request.notes")}
						value={notes}
						maxLength={2000}
						onChange={(e) => setNotes(e.target.value)}
					/>
					{failed !== null && (
						<p role="alert" className="text-[12.5px] text-danger-fg">
							{failed}
						</p>
					)}
				</div>
			</ConfirmDialog>
		</>
	);
}

/** The order's shipments, what the store may do with each, and their proof. */
export function OrderShipments({
	orderId,
	storeId,
	timeZone,
}: {
	orderId: string;
	storeId: string;
	timeZone: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const shipments = useQuery({
		queryKey: shipmentsKey(storeId, orderId),
		queryFn: ({ signal }) => api.listPortalOrderShipments(orderId, { signal }),
	});
	const rows = shipments.data?.items ?? [];
	if (rows.length === 0) {
		return null;
	}
	return (
		<>
			<section aria-labelledby="order-shipments" className="card p-4">
				<h2 id="order-shipments" className="mb-3 text-[15px] font-bold text-fg">
					{t("portal.returns.shipments.title")}
				</h2>
				<ul className="flex flex-col gap-2">
					{rows.map((sh) => (
						<ShipmentRow key={sh.shipmentId} shipment={sh} orderId={orderId} storeId={storeId} />
					))}
				</ul>
			</section>
			{rows
				.filter((sh) => sh.status === "delivered" || sh.status === "partially_delivered")
				.map((sh) => (
					<PodCard key={sh.shipmentId} shipment={sh} storeId={storeId} timeZone={timeZone} />
				))}
		</>
	);
}

function ShipmentRow({
	shipment,
	orderId,
	storeId,
}: {
	shipment: PortalShipment;
	orderId: string;
	storeId: string;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const { store } = useStoreScope();
	const [asking, setAsking] = useState<"recall" | "approve_rto" | null>(null);
	const [failed, setFailed] = useState<string | null>(null);
	const writable = levelOf(store, "returns") === "write";
	const label = shipment.trackingNumber ?? shipment.shipmentId;
	return (
		<li className="flex flex-wrap items-center gap-3 text-[13px]">
			<span className="font-mono">{label}</span>
			<StatusChip tone={shipment.awaitingRto ? "warning" : "neutral"} size="sm">
				{t(`orders.status.${shipment.status}`)}
			</StatusChip>
			{shipment.returnLeg && (
				<span className="text-fg-muted">{t("portal.returns.shipments.returnLeg")}</span>
			)}
			<span className="text-fg-muted">
				{t("portal.returns.rto.attempts", { count: shipment.attemptCount })}
			</span>
			{writable && shipment.awaitingRto && (
				<Button size="sm" variant="primary" onClick={() => setAsking("approve_rto")}>
					{t("portal.returns.rto.approve")}
				</Button>
			)}
			{writable && !shipment.awaitingRto && shipment.recallable && (
				<Button size="sm" variant="secondary" onClick={() => setAsking("recall")}>
					{t("portal.returns.recall.button")}
				</Button>
			)}
			{asking !== null && (
				<ConfirmDialog
					open
					onOpenChange={(open) => {
						if (!open) {
							setAsking(null);
							setFailed(null);
						}
					}}
					title={t(`portal.returns.${asking === "recall" ? "recall" : "rto"}.confirmTitle`, {
						shipment: label,
					})}
					description={t(`portal.returns.${asking === "recall" ? "recall" : "rto"}.confirmBody`)}
					confirmLabel={t(
						asking === "recall" ? "portal.returns.recall.button" : "portal.returns.rto.approve",
					)}
					cancelLabel={t("ui.actions.cancel")}
					closeLabel={t("ui.actions.close")}
					onConfirm={async () => {
						try {
							await api.requestPortalReturn({ kind: asking, shipmentId: shipment.shipmentId });
						} catch (error) {
							setFailed(error instanceof ApiError ? error.message : t("portal.returns.refused"));
							throw error;
						}
						await Promise.all([
							queries.invalidateQueries({ queryKey: shipmentsKey(storeId, orderId) }),
							queries.invalidateQueries({ queryKey: returnsKey(storeId) }),
						]);
					}}
				>
					{failed !== null && (
						<p role="alert" className="text-[12.5px] text-danger-fg">
							{failed}
						</p>
					)}
				</ConfirmDialog>
			)}
		</li>
	);
}

/** Proof of delivery (DA-10-05): what the driver captured, never who the driver was. */
function PodCard({
	shipment,
	storeId,
	timeZone,
}: {
	shipment: PortalShipment;
	storeId: string;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const pod = useQuery({
		queryKey: ["portal", storeId, "orders", "pod", shipment.shipmentId],
		queryFn: ({ signal }) => api.getPortalShipmentPod(shipment.shipmentId, { signal }),
		// The links are signed for five minutes: refetched before they expire.
		staleTime: 4 * 60_000,
		refetchInterval: 4 * 60_000,
	});
	const records = pod.data?.records ?? [];
	if (records.length === 0) {
		return null;
	}
	const label = shipment.trackingNumber ?? shipment.shipmentId;
	return (
		<section aria-labelledby={`pod-${shipment.shipmentId}`} className="card p-4">
			<div className="mb-3 flex flex-wrap items-center justify-between gap-2">
				<h2 id={`pod-${shipment.shipmentId}`} className="text-[15px] font-bold text-fg">
					{t("portal.returns.pod.title")}
				</h2>
				<ReportProblemButton source="shipment" id={shipment.shipmentId} label={label} />
			</div>
			<div className="flex flex-col gap-4">
				{records.map((record) => (
					<div key={record.capturedAt} className="flex flex-col gap-2">
						<div className="flex flex-wrap gap-2">
							{record.otpVerified && (
								<StatusChip tone="success" size="sm">
									{t("portal.returns.pod.otp")}
								</StatusChip>
							)}
							{record.fallbackUsed && (
								<StatusChip tone="warning" size="sm">
									{t("portal.returns.pod.fallback")}
								</StatusChip>
							)}
							{record.capturedAtAddress !== null && record.capturedAtAddress !== undefined && (
								<StatusChip tone={record.capturedAtAddress ? "success" : "warning"} size="sm">
									{t(
										record.capturedAtAddress
											? "portal.returns.pod.atAddress"
											: "portal.returns.pod.notAtAddress",
									)}
								</StatusChip>
							)}
						</div>
						<KeyValueList
							rows={[
								{
									label: t("portal.returns.pod.recipient"),
									value: record.recipientName ?? "—",
								},
								{
									label: t("portal.returns.pod.capturedAt"),
									value: formatDateTime(record.capturedAt, { locale, timeZone }),
								},
							]}
						/>
						<Photos files={record.photos} label={t("portal.returns.pod.photo")} />
						{record.signature && (
							<div>
								<p className="text-[12.5px] text-fg-muted">{t("portal.returns.pod.signature")}</p>
								<Photos files={[record.signature]} label={t("portal.returns.pod.signature")} />
							</div>
						)}
					</div>
				))}
			</div>
		</section>
	);
}
